import { useEffect, useState } from 'react';
import { usePlatform } from '@localagent/platform';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { LoadingState, ShogunSpinner } from '@localagent/ui/components/shogun-spinner';
import { desktopDownloadsUrl } from '../boot/deployment-keys';

const CALLOUT = 'Install the Mac app to enable local workspaces and desktop tooling.';

export interface DownloadManifest {
  placeholder: boolean;
  reason?: string;
  stack?: string;
  latest?: string;
  latest_dmg?: string;
  latest_pkg?: string;
  latest_uninstall?: string;
  sha256?: string;
  size_bytes?: number;
  pkg_sha256?: string;
  pkg_size_bytes?: number;
  built_at?: string;
}

export interface DownloadForMacProps {
  hideOnDesktop?: boolean;
  compact?: boolean;
}

function formatSize(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return '';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

function formatVersion(latest: string | undefined): string {
  if (!latest) return '';
  return latest.startsWith('v') ? latest : `v${latest}`;
}

function DownloadGlyph() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}

export function DownloadForMac({ hideOnDesktop, compact }: DownloadForMacProps = {}) {
  const platform = usePlatform();
  const isDesktop = platform.type === 'desktop';
  const suppress = hideOnDesktop ?? isDesktop;

  const [manifest, setManifest] = useState<DownloadManifest | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (suppress) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // (la ADR-6ff8f)
        const resp = await fetch(`${desktopDownloadsUrl()}/manifest.json`, { cache: 'no-store' });
        if (!resp.ok) {
          throw new Error(`manifest fetch failed: ${resp.status}`);
        }
        const body = (await resp.json()) as DownloadManifest;
        if (!cancelled) setManifest(body);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setManifest({ placeholder: true, reason: (err as Error).message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [suppress]);

  if (suppress) {
    return null;
  }

  if (!manifest) {
    return (
      <div data-testid="download-for-mac" data-state="loading" className="text-xs text-muted-foreground">
        {compact ? <ShogunSpinner size="sm" /> : <LoadingState variant="inline" label="Loading download…" />}
      </div>
    );
  }

  if (manifest.placeholder) {
    if (compact) {
      return null;
    }
    return (
      <div
        data-testid="download-for-mac"
        data-state="placeholder"
        className="w-full max-w-sm rounded-lg bg-card p-4 text-sm text-muted-foreground"
      >
        <p className="font-medium text-foreground">Get the Mac app</p>
        <p className="mt-1">{CALLOUT}</p>
        <p className="mt-2 text-xs">NOT FOR DISTRIBUTION — dev build</p>
        {manifest.reason && <p className="mt-1 text-xs">{manifest.reason}</p>}
      </div>
    );
  }

  const href = manifest.latest_dmg;
  if (!href) {
    throw new Error('download manifest is live but has no latest_dmg');
  }
  const version = formatVersion(manifest.latest);
  const sizeText = formatSize(manifest.size_bytes);
  const meta = [version, sizeText].filter(Boolean).join(' · ');
  const tooltip = meta ? `${CALLOUT} ${meta}` : CALLOUT;

  if (compact) {
    return (
      <Tooltip label={tooltip}>
        <a
          data-testid="download-for-mac"
          data-state="live"
          data-version={manifest.latest}
          data-sha256={manifest.sha256}
          href={href}
          aria-label={`Download Mac app. ${tooltip}`}
          className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <DownloadGlyph />
          Download App
          {error && <span className="sr-only">manifest error: {error}</span>}
        </a>
      </Tooltip>
    );
  }

  return (
    <div
      data-testid="download-for-mac"
      data-state="live"
      data-version={manifest.latest}
      data-sha256={manifest.sha256}
      className="w-full max-w-sm rounded-lg bg-card p-4 text-card-foreground shadow-sm"
    >
      <p className="text-sm font-semibold">Get the Mac app</p>
      <p className="mt-1 text-sm text-muted-foreground">{CALLOUT}</p>
      {meta && <p className="mt-1 text-xs text-muted-foreground">{meta}</p>}
      <a
        href={href}
        className="mt-3 inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        Download{version ? ` ${version}` : ''}
      </a>
      {error && <span className="sr-only">manifest error: {error}</span>}
    </div>
  );
}
